import { Check } from 'lucide-react';
import { Icon } from '@/shared/components/Icon';
import { cn } from '@/shared/helpers/cn';
import styles from './Stepper.module.scss';

export interface Step {
  id: string;
  label: string;
  /** Uma linha dizendo o que se resolve neste passo. */
  hint?: string;
}

export interface StepperProps {
  steps: readonly Step[];
  activeId: string;
  /** Nome do conjunto, para leitores de tela. */
  label: string;
  /**
   * Volta a um passo ja concluido. Sem isto os passos sao so indicacao.
   *
   * Passo adiante nunca e clicavel: o que valida o passo atual e o botao de
   * avancar, e pular por cima dele deixaria o formulario passar sem validacao.
   */
  onGoTo?: (id: string) => void;
  className?: string;
}

/**
 * Indicador de passos de um formulario longo.
 *
 * Diz **onde a pessoa esta**, quantos passos faltam e o que ja ficou para tras.
 * O passo concluido leva um "check" e o atual, o proprio numero em destaque —
 * dois sinais alem da cor, que sozinha nao comunica estado.
 *
 * A lista e um `<ol>`: a ordem e a informacao. O passo atual carrega
 * `aria-current="step"`, que e o que o leitor de tela anuncia ao chegar nele.
 */
export function Stepper({ steps, activeId, label, onGoTo, className }: StepperProps) {
  const atual = steps.findIndex((passo) => passo.id === activeId);

  return (
    <nav aria-label={label} className={cn(styles.root, className)}>
      <ol className={styles.lista}>
        {steps.map((passo, indice) => {
          const concluido = indice < atual;
          const ativo = indice === atual;
          const alcancavel = concluido && onGoTo !== undefined;

          const conteudo = (
            <>
              <span className={styles.marca} aria-hidden="true">
                {concluido ? <Icon as={Check} size={16} /> : indice + 1}
              </span>
              <span className={styles.textos}>
                <span className={styles.rotulo}>{passo.label}</span>
                {passo.hint ? <span className={styles.dica}>{passo.hint}</span> : null}
              </span>
            </>
          );

          return (
            <li
              key={passo.id}
              className={cn(styles.passo, ativo && styles.ativo, concluido && styles.concluido)}
              aria-current={ativo ? 'step' : undefined}
            >
              {alcancavel ? (
                <button
                  type="button"
                  className={styles.gatilho}
                  onClick={() => onGoTo(passo.id)}
                >
                  {conteudo}
                  <span className={styles.srOnly}>— concluído, voltar a este passo</span>
                </button>
              ) : (
                <span className={styles.gatilho}>
                  {conteudo}
                  {concluido ? <span className={styles.srOnly}>— concluído</span> : null}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
